import type { FeatureStatus } from "common/constants/features";
import { mapFeatureStatusToDisplayTextAndColor } from "lib/components/admin/features/helpers";
import type { Breadcrumb } from "lib/components/navigation/BreadcrumbMenu";
import BreadcrumbMenu from "lib/components/navigation/BreadcrumbMenu";
import type { ReactNode } from "react";
import Capsule from "ui/components/Capsule";
import type { TabbedMenuItem } from "ui/components/TabbedMenu";
import TabbedMenu from "ui/components/TabbedMenu";
import cx from "ui/cx";
import styles from "./BuilderHeader.module.scss";

export interface BuilderHeaderProps {
  header?: string | ReactNode;
  subheaderItems?: ReactNode[];
  breadcrumbs?: Breadcrumb[];
  menuItems?: TabbedMenuItem[];
  headerActions?: ReactNode;
  wide?: boolean;
  betaProduct?: boolean;
  /** Feature status to display next to the header (e.g., "alpha", "open_beta") */
  featureStatus?: FeatureStatus;
  noBottomBorder?: boolean;
  noHorizontalMargin?: boolean;
}

export default function BuilderHeader({
  header,
  breadcrumbs,
  menuItems,
  subheaderItems,
  headerActions,
  wide = false,
  betaProduct = false,
  featureStatus,
  noBottomBorder = false,
  noHorizontalMargin = false,
}: BuilderHeaderProps) {
  const statusDisplay =
    featureStatus && featureStatus !== "general_availability"
      ? mapFeatureStatusToDisplayTextAndColor(featureStatus)
      : null;
  return (
    <div className={cx({ styles, wrapper: true, wide, noBottomBorder, noHorizontalMargin })}>
      <div className="header-container">
        <div className="builder-header">
          {breadcrumbs && breadcrumbs.length > 0 && <BreadcrumbMenu breadcrumbs={breadcrumbs} />}
          <div className="header-row">
            <div className="header-left">
              <div className="header-text" data-testid="header-text">
                {header}
              </div>
              {betaProduct && (
                <span className={styles.betaCapsuleWrapper}>
                  <Capsule label="BETA" size="small" color="white" backgroundColor="stoneDark" />
                </span>
              )}
              {statusDisplay && (
                <span className={styles.betaCapsuleWrapper}>
                  <Capsule label={statusDisplay.text} size="mini" color="white" backgroundColor={statusDisplay.color} />
                </span>
              )}
              <div className="subheader-text">
                {subheaderItems?.map((subheader, i) => <div key={`subheader-items-${i}`}>{subheader}</div>)}
              </div>
            </div>
            <div className="header-right">{headerActions}</div>
          </div>
        </div>
        {menuItems && menuItems.length > 0 && <TabbedMenu menuItems={menuItems} />}
      </div>
    </div>
  );
}
